import { useMemo } from 'react';

/**
 * BingoBall Component
 * Displays a single bingo ball with letter and number
 * Colors follow traditional bingo: B=blue, I=red, N=gray, G=green, O=yellow
 * Superball (76) has special golden styling
 */

const BALL_COLORS = {
  B: {
    bg: 'bg-blue-500',
    border: 'border-blue-600',
    text: 'text-white',
    shadow: 'shadow-blue-500/30',
    glow: 'shadow-blue-400/50',
    darkBg: 'dark:bg-blue-600',
  },
  I: {
    bg: 'bg-red-500',
    border: 'border-red-600',
    text: 'text-white',
    shadow: 'shadow-red-500/30',
    glow: 'shadow-red-400/50',
    darkBg: 'dark:bg-red-600',
  },
  N: {
    bg: 'bg-gray-100',
    border: 'border-gray-300',
    text: 'text-gray-800',
    shadow: 'shadow-gray-500/30',
    glow: 'shadow-gray-400/50',
    darkBg: 'dark:bg-gray-400',
    darkText: 'dark:text-gray-900',
  },
  G: {
    bg: 'bg-green-500',
    border: 'border-green-600',
    text: 'text-white',
    shadow: 'shadow-green-500/30',
    glow: 'shadow-green-400/50',
    darkBg: 'dark:bg-green-600',
  },
  O: {
    bg: 'bg-yellow-400',
    border: 'border-yellow-500',
    text: 'text-gray-900',
    shadow: 'shadow-yellow-500/30',
    glow: 'shadow-yellow-400/50',
    darkBg: 'dark:bg-yellow-500',
  },
  S: {
    bg: 'bg-gradient-to-br from-amber-400 via-yellow-300 to-amber-500',
    border: 'border-amber-500',
    text: 'text-amber-900',
    shadow: 'shadow-amber-500/50',
    glow: 'shadow-amber-400/70',
    darkBg: 'dark:from-amber-500 dark:via-yellow-400 dark:to-amber-600',
    special: true,
  },
};

function getBallLetter(number) {
  if (number === 76) return 'S'; // Superball
  if (number >= 1 && number <= 15) return 'B';
  if (number >= 16 && number <= 30) return 'I';
  if (number >= 31 && number <= 45) return 'N';
  if (number >= 46 && number <= 60) return 'G';
  if (number >= 61 && number <= 75) return 'O';
  return '?';
}

export default function BingoBall({ 
  number, 
  size = 'md', 
  drawn = false, 
  selected = false,
  animated = false,
  showLetter = true,
  onClick,
  className = '',
}) {
  const letter = useMemo(() => getBallLetter(number), [number]);
  const colors = BALL_COLORS[letter] || BALL_COLORS.N;
  const isSuperball = number === 76;

  const sizeClasses = {
    xs: 'w-6 h-6 text-[8px]',
    sm: 'w-8 h-8 text-xs',
    md: 'w-12 h-12 text-sm',
    lg: 'w-16 h-16 text-base',
    xl: 'w-20 h-20 text-lg',
    '2xl': 'w-24 h-24 text-xl',
  };

  const drawnStyles = drawn ? 'opacity-30 grayscale' : '';
  const selectedStyles = selected ? `ring-4 ring-amber-400 ring-offset-2 scale-110 ${colors.glow}` : '';
  const animatedStyles = animated ? 'animate-bounce' : '';
  const superballStyles = isSuperball ? 'animate-pulse ring-2 ring-amber-300 ring-offset-1' : '';
  const clickableStyles = onClick ? 'cursor-pointer hover:scale-105 transition-transform' : '';

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!onClick || drawn}
      className={`
        ${sizeClasses[size]}
        ${colors.bg}
        ${colors.darkBg || ''}
        ${colors.border}
        ${colors.text}
        ${colors.darkText || ''}
        border-2 rounded-full
        flex flex-col items-center justify-center
        font-bold shadow-lg ${colors.shadow}
        ${drawnStyles}
        ${selectedStyles}
        ${animatedStyles}
        ${superballStyles}
        ${clickableStyles}
        ${className}
        relative overflow-hidden
      `}
    >
      {/* Highlight shine effect */}
      <div className="absolute top-1 left-1/4 w-1/3 h-1/4 bg-white/40 rounded-full blur-sm" />
      
      {showLetter && (
        <span className="leading-none font-black">{isSuperball ? '★' : letter}</span>
      )}
      <span className="leading-none font-bold">{isSuperball ? 'SB' : number}</span>
    </button>
  );
}

// Export utility functions
export { getBallLetter, BALL_COLORS };
